import { format, isThisWeek, isToday } from "date-fns";
import type { ReadonlyURLSearchParams } from "next/navigation";

export function formatMessageTime(input: string | number | Date): string {
  const d = input instanceof Date ? input : new Date(input);
  if (isToday(d)) {
    return format(d, "HH:mm");
  }
  if (isThisWeek(d, { weekStartsOn: 1 })) {
    return format(d, "EEE HH:mm");
  }
  return format(d, "d MMM HH:mm");
}

function _stripSpecialCharacters(inputString: string) {
  // Use a regular expression to replace all non-alphanumeric characters except hyphen, space, dot,and parentheses with an empty string
  return inputString?.replace(/[^a-zA-Z0-9\s.()-]/g, "");
}

// Create query string
const _createQueryString = (
  params: Record<string, string | number | null>,
  searchParams?: URLSearchParams | ReadonlyURLSearchParams
) => {
  const newSearchParams = new URLSearchParams(searchParams?.toString());

  for (const [key, value] of Object.entries(params)) {
    if (value === null) {
      newSearchParams.delete(key);
    } else {
      newSearchParams.set(key, String(value));
    }
  }

  return newSearchParams.toString();
};

const _createUrl = (currentPath: string, newPath: string): string => {
  const hasQueries = currentPath.indexOf("?");
  const searchQuery = hasQueries >= 0 ? currentPath.substring(hasQueries) : "";
  const params = new URLSearchParams(searchQuery);
  const paramsStr = params.toString() !== "" ? `?${params.toString()}` : "";
  return `${newPath}${paramsStr}`;
};

function _formatDate(input: string | number): string {
  const date = new Date(input);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

// Cache currency formatters by currency code — building one is expensive, so
// reuse them across calls instead of rebuilding on every invocation.
const currencyFormatters = new Map<string, Intl.NumberFormat>();
function getCurrencyFormatter(currency: string): Intl.NumberFormat {
  let fmt = currencyFormatters.get(currency);
  if (!fmt) {
    // oxlint-disable-next-line react-doctor/js-hoist-intl -- intentionally cached in the Map above; rebuilt at most once per currency
    fmt = new Intl.NumberFormat("en-za", { style: "currency", currency });
    currencyFormatters.set(currency, fmt);
  }
  return fmt;
}

export function formatCurrency(
  input: string | number,
  currency = "ZAR"
): string {
  return getCurrencyFormatter(currency).format(Number(input));
}

const _svgToPngURL = (svg: string) =>
  new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
      URL.revokeObjectURL(img.src);
    };
    img.onerror = (e) => {
      reject(e);
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  });

export const download = (url: string, filename: string, extension = "pdf") => {
  if (!url) {
    throw new Error("Resource URL not provided! You need to provide one");
  }

  fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      const blobURL = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobURL;

      if (filename?.length) {
        a.download = `${filename.replace(" ", "_")}.${extension}`;
      }
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobURL);
    });
};

export type UnknownArrayOrObject = unknown[] | Record<string, unknown>;

// @see https://github.com/react-hook-form/react-hook-form/discussions/1991#discussioncomment-351784
// export const dirtyValues = (
//   dirtyFields: UnknownArrayOrObject | boolean,
//   allValues: UnknownArrayOrObject,
// ): UnknownArrayOrObject => {
//   // NOTE: Recursive function.

//   // If *any* item in an array was modified, the entire array must be submitted, because there's no
//   // way to indicate "placeholders" for unchanged elements. `dirtyFields` is `true` for leaves.
//   if (dirtyFields === true || Array.isArray(dirtyFields)) {
//     return allValues;
//   }

//   // Here, we have an object.
//   return Object.fromEntries(
//     Object.keys(dirtyFields).map((key) => [
//       key,
//       dirtyValues(dirtyFields[key], allValues[key]),
//     ]),
//   );
// };

export type DirtyFieldsType =
  | boolean
  | null
  | {
      [key: string]: DirtyFieldsType;
    }
  | DirtyFieldsType[];

export function getDirtyValues<T extends Record<string, unknown>>(
  dirtyFields: Readonly<Partial<Record<string, unknown>>>,
  values: T
): Partial<T> {
  const dirtyValues = {} as Partial<T>;

  for (const key of Object.keys(dirtyFields)) {
    const value = dirtyFields[key];
    if (!value) {
      continue;
    }
    const isObject = typeof value === "object";
    const isArray = Array.isArray(value);
    const nestedValue =
      isObject && !isArray
        ? getDirtyValues(
            value as Partial<Record<keyof T, DirtyFieldsType>>,
            values[key] as T
          )
        : values[key];
    dirtyValues[key as keyof T] = (
      isArray ? values[key] : nestedValue
    ) as T[keyof T];
  }

  return dirtyValues;
}

export const isDevelopment = () => process.env.NODE_ENV === "development";
